import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { Direction2D, Line2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import type { LayoutLinePickingTarget, LayoutPointPickingTarget } from '../../../store/slices/applicationSlice';
import { EntitySnapTarget, SerializedEntitySnapTarget } from '../../layout-snapping/EntitySnapTarget';
import { CardinalDirection, vectorIsPointingInDirection } from '../directions';

export interface ParallelInsertionWorkLine {
  type: 'parallel';
  base?: SerializedEntitySnapTarget;
  offset?: number;
  direction?: CardinalDirection;
}

export interface PointToPointInsertionWorkLine {
  type: 'point-to-point';

  segment: boolean;

  basePoint?: Point2DInterface;
  endPoint?: Point2DInterface;
}

export interface PointAngleInsertionWorkLine {
  type: 'point-angle';
  basePoint?: Point2DInterface;
  angle?: number;
}

export interface PerpendicularInsertionWorkLine {
  type: 'perpendicular';
  base?: SerializedEntitySnapTarget; // the line the new line is perpendicular to
  point?: Point2DInterface; // the point the new line passes through
}

export interface DivisionInsertionWorkLine {
  type: 'division';
  base?: SerializedEntitySnapTarget;
  other?: SerializedEntitySnapTarget;
  quantity: number;
}

export interface ExactInsertionWorkLine {
  type: 'exact';
  base?: SerializedEntitySnapTarget;
}

export type InsertionWorkLine =
  | ParallelInsertionWorkLine
  | PointToPointInsertionWorkLine
  | PointAngleInsertionWorkLine
  | PerpendicularInsertionWorkLine
  | DivisionInsertionWorkLine
  | ExactInsertionWorkLine;

export type ActualizedInsertionWorkLine = Line2D | LineSegment2D;

/** Returns the empty insertion line a user starts from when manually choosing the given line type. */
export function getDefaultInsertionWorkLine(
  type: InsertionWorkLine['type'],
  options: { segment: boolean; divisionQuantity: number },
): InsertionWorkLine {
  switch (type) {
    case 'parallel':
    case 'point-angle':
    case 'perpendicular':
    case 'exact':
      return { type };

    case 'division':
      return { type, quantity: options.divisionQuantity };

    case 'point-to-point':
      return { type, segment: options.segment };

    default:
      assertExhaustiveSwitch(type);
  }
}

export function applyPickedPointToInsertionWorkLine(
  line: InsertionWorkLine,
  target: LayoutPointPickingTarget,
  point: Point2DInterface,
): InsertionWorkLine | null {
  switch (target) {
    case 'insertion-work-line-point-to-point-base':
      return line.type === 'point-to-point' ? { ...line, basePoint: point } : null;

    case 'insertion-work-line-point-to-point-end':
      return line.type === 'point-to-point' ? { ...line, endPoint: point } : null;

    case 'insertion-work-line-point-angle-base':
      return line.type === 'point-angle' ? { ...line, basePoint: point } : null;

    case 'insertion-work-line-perpendicular-point':
      return line.type === 'perpendicular' ? { ...line, point } : null;

    default:
      return null; // not a work line point picking target
  }
}

export function applyPickedLineToInsertionWorkLine(
  line: InsertionWorkLine,
  target: LayoutLinePickingTarget,
  pickedLine: SerializedEntitySnapTarget,
): InsertionWorkLine | null {
  switch (target) {
    case 'insertion-work-line-parallel-base-line':
      return line.type === 'parallel' ? { ...line, base: pickedLine } : null;

    case 'insertion-work-line-perpendicular-base-line':
      return line.type === 'perpendicular' ? { ...line, base: pickedLine } : null;

    case 'insertion-work-line-division-line-1':
      return line.type === 'division' ? { ...line, base: pickedLine } : null;

    case 'insertion-work-line-division-line-2':
      return line.type === 'division' ? { ...line, other: pickedLine } : null;

    case 'insertion-work-line-exact-base-line':
      return line.type === 'exact' ? { ...line, base: pickedLine } : null;

    default:
      return null; // not a work line line picking target
  }
}

export function tryActualizeWorkLines(insertionLine: InsertionWorkLine): ActualizedInsertionWorkLine[] {
  try {
    switch (insertionLine.type) {
      case 'parallel': {
        if (insertionLine.base == null) return [];

        const baseLine = EntitySnapTarget.fromObject(insertionLine.base).getDefiningLine();

        let offsetVector = baseLine.direction.rotateQuarterTurn().toVector(insertionLine.offset ?? 0);
        if (insertionLine.direction && !vectorIsPointingInDirection(offsetVector, insertionLine.direction)) {
          offsetVector = offsetVector.reverse();
        }

        return [new Line2D(baseLine.basePoint.translate(offsetVector), baseLine.direction)];
      }

      case 'point-to-point': {
        if (insertionLine.basePoint == null || insertionLine.endPoint == null) return [];

        return [
          insertionLine.segment
            ? LineSegment2D.fromPointObjects(insertionLine.basePoint, insertionLine.endPoint)
            : Line2D.fromPointObjects(insertionLine.basePoint, insertionLine.endPoint),
        ];
      }

      case 'point-angle': {
        if (insertionLine.basePoint == null) return [];

        const basePoint = Point2D.fromObject(insertionLine.basePoint);
        const angle = Direction2D.fromAngle(insertionLine.angle ?? 0);

        return [new Line2D(basePoint, angle)];
      }

      case 'perpendicular': {
        if (insertionLine.base == null || insertionLine.point == null) return [];

        const baseLine = EntitySnapTarget.fromObject(insertionLine.base).getDefiningLine();
        const point = Point2D.fromObject(insertionLine.point);

        return [new Line2D(point, baseLine.direction.rotateQuarterTurn())];
      }

      case 'division':
        return actualizeDivisionWorkLines(insertionLine);

      case 'exact': {
        if (insertionLine.base == null) return [];

        return [EntitySnapTarget.fromObject(insertionLine.base).getDefiningLine()];
      }

      default:
        assertExhaustiveSwitch(insertionLine);
    }
  } catch {
    // A crash when calculating the lines means that the draft is invalid.
    return [];
  }
}

function actualizeDivisionWorkLines(insertionLine: DivisionInsertionWorkLine): Line2D[] {
  if (insertionLine.base == null || insertionLine.other == null || insertionLine.quantity < 1) return [];

  const line1 = EntitySnapTarget.fromObject(insertionLine.base).getDefiningLine();
  const line2 = EntitySnapTarget.fromObject(insertionLine.other).getDefiningLine();

  if (!line1.isParallelTo(line2) || line1.equals(line2)) return [];

  const line1Point = line1.basePoint;
  const line2Point = new Line2D(line1Point, line1.direction.rotateQuarterTurn()).intersectWithLine(line2)!;

  const perpendicularSegment = new LineSegment2D(line1Point, line2Point);

  const lines: Line2D[] = [];
  for (let i = 1; i <= insertionLine.quantity; i++) {
    lines.push(
      new Line2D(
        perpendicularSegment.getPointAt(perpendicularSegment.length * (i / (insertionLine.quantity + 1)))!,
        line1.direction,
      ),
    );
  }

  return lines;
}
